import type { CheckinUser } from '@bp/utils';
import { DateTime } from 'luxon';
import React from 'react';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import Tooltip from 'react-bootstrap/esm/Tooltip';

interface CheckinAvatarProps extends React.ComponentPropsWithoutRef<'div'> {
    user?: CheckinUser | null;
    showEmptyIfNoUser?: boolean;
}

function getInitials(name?: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getTooltipAdditional(user: CheckinUser): string | null {
    if (!user.role) return null;
    let timePart = '';

    if (user.untilISO) {
        const diff = DateTime.fromISO(user.untilISO).diffNow();
        if (diff.as('hour') >= 1) {
            timePart = ` for next ${Math.round(diff.as('hour'))} hours`;
        } else if (diff.as('minute') >= 1) {
            timePart = ` for next ${Math.round(diff.as('minute'))} minutes`;
        }
    }
    return `as ${user.role}${timePart}`;
}

export function CheckinAvatar({ user, showEmptyIfNoUser }: CheckinAvatarProps): JSX.Element {
    const [imgError, setImgError] = React.useState(false);

    const avatarStyle = React.useMemo<React.CSSProperties>(
        () => ({
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: `2px solid ${user?.email ? (user?.role && String(user.role) === 'ONSITE' ? 'green' : 'blue') : 'transparent'}`,
            objectFit: 'cover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            color: '#fff',
            fontWeight: 600,
            background: imgError ? '#ccc' : undefined
        }),
        [user?.email, user?.role, imgError]
    );

    const initials = React.useMemo(() => getInitials(user?.displayName), [user?.displayName]);

    if (!user) {
        if (showEmptyIfNoUser) {
            return (
                <span
                    style={{
                        ...avatarStyle,
                        color: '#ccc',
                        background: '#f5f5f5',
                        border: '2px dashed #ccc'
                    }}
                />
            );
        }
        return <></>;
    }

    const avatar = !imgError ? (
        <img src={user.avatarUrl} alt={user.displayName} style={avatarStyle} onError={() => setImgError(true)} />
    ) : (
        <span style={avatarStyle}>{initials}</span>
    );

    if (!user.email) return <>{avatar}</>;

    const tooltipAdditional = getTooltipAdditional(user);

    return (
        <OverlayTrigger
            overlay={
                <Tooltip id={`tooltip-${user.email}`}>
                    <div>{user.displayName}</div>
                    {tooltipAdditional && <div style={{ fontSize: 12, color: '#aaa' }}>{tooltipAdditional}</div>}
                </Tooltip>
            }
        >
            <span>{avatar}</span>
        </OverlayTrigger>
    );
}
